import { useMemo, useState } from 'react';
import { Input, Select, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined, WarningFilled } from '@ant-design/icons';
import { SeverityTag } from '@/oversight/badges';
import { formatRelative } from '@/lib/time';
import { ALERT_SEVERITIES, ALERT_STATUSES, routeFailure, type AlertView } from '@/api/alerts';
import { TableSkeleton } from '@/components/TableSkeleton';

const { Text } = Typography;

/** Status → antd Tag colour. Tokens render VERBATIM next to the colour (never colour alone). */
const STATUS_COLOR: Record<string, string> = {
  OPEN: 'gold',
  ROUTED: 'blue',
  ACKNOWLEDGED: 'purple',
  CLOSED: 'green',
  SUPPRESSED: 'default',
};

export interface AlertRegisterFilters {
  status?: string;
  severity?: string;
  type?: string;
}

export interface AlertRegisterProps {
  alerts: AlertView[];
  loading: boolean;
  /** Server-side filters (status/severity/type — what GET /v1/alerts accepts). */
  filters: AlertRegisterFilters;
  onFiltersChange: (next: AlertRegisterFilters) => void;
  /** Distinct types present in the loaded page — options for the type filter. */
  typeOptions: string[];
  /** Row activation (click / Enter) → the detail drawer. */
  onOpen: (alert: AlertView) => void;
}

/**
 * The alert register (OPSFL-ALUI-1) — a filterable table over GET /v1/alerts. Presentational:
 * the page owns fetching; this component owns the filter bar, the quick text filter
 * (alert_key / rule / signal — CLIENT-side, over the server-capped page) and the row language.
 * Routing failures render LOUD (I4): a matched rule that could not start its flow is a red
 * "ROUTING FAILED" marker on the row, never a footnote.
 */
export function AlertRegister({
  alerts,
  loading,
  filters,
  onFiltersChange,
  typeOptions,
  onOpen,
}: AlertRegisterProps) {
  const [quick, setQuick] = useState('');
  const now = Date.now();

  const visible = useMemo(() => {
    const q = quick.trim().toLowerCase();
    if (!q) return alerts;
    return alerts.filter((a) =>
      [a.alertKey, a.type, a.routedRuleId, a.routedRunId, a.signalId, a.workItemId, a.entityId]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [alerts, quick]);

  const columns: ColumnsType<AlertView> = [
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 104,
      render: (v: string) => <SeverityTag severity={v} />,
    },
    {
      title: 'Alert',
      key: 'alert',
      render: (_, a) => (
        <div style={{ minWidth: 200 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5 }}>{a.type}</div>
          <Text className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
            {a.alertKey}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (v: string, a) => {
        const failure = routeFailure(a);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Tag color={STATUS_COLOR[v] ?? 'default'} style={{ marginInlineEnd: 0 }}>
              {v}
            </Tag>
            {failure && (
              <Tooltip title={failure}>
                <Tag
                  color="error"
                  icon={<WarningFilled aria-hidden />}
                  style={{ marginInlineEnd: 0, fontWeight: 700 }}
                >
                  ROUTING FAILED
                </Tag>
              </Tooltip>
            )}
            {a.disposition && (
              <Tag style={{ marginInlineEnd: 0 }} title={`dispositioned ${a.disposition}`}>
                {a.disposition}
              </Tag>
            )}
          </span>
        );
      },
    },
    { title: 'Source', dataIndex: 'source', key: 'source', width: 110 },
    {
      title: 'Routed to',
      key: 'routedTo',
      width: 180,
      render: (_, a) =>
        a.workItemId ? (
          <Text className="ol-mono" style={{ fontSize: 11 }} title="Work item — open the drawer to jump to the Workbench">
            {a.workItemId}
          </Text>
        ) : a.routedRunId ? (
          <Text className="ol-mono" style={{ fontSize: 11 }} title="Run started by a routing rule">
            {a.routedRunId}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            not routed
          </Text>
        ),
    },
    {
      title: 'Raised',
      dataIndex: 'raisedAt',
      key: 'raisedAt',
      width: 110,
      render: (v: string | null) => (
        <Text style={{ fontSize: 12 }} title={v ?? undefined}>
          {formatRelative(v, now)}
        </Text>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <Select
          aria-label="Filter by status"
          placeholder="Status"
          allowClear
          style={{ width: 150 }}
          value={filters.status}
          onChange={(v) => onFiltersChange({ ...filters, status: v })}
          options={ALERT_STATUSES.map((s) => ({ value: s, label: s }))}
        />
        <Select
          aria-label="Filter by severity"
          placeholder="Severity"
          allowClear
          style={{ width: 130 }}
          value={filters.severity}
          onChange={(v) => onFiltersChange({ ...filters, severity: v })}
          options={ALERT_SEVERITIES.map((s) => ({ value: s, label: s }))}
        />
        <Select
          aria-label="Filter by type"
          placeholder="Type"
          allowClear
          showSearch
          style={{ width: 190 }}
          value={filters.type}
          onChange={(v) => onFiltersChange({ ...filters, type: v })}
          options={typeOptions.map((t) => ({ value: t, label: t }))}
        />
        <Input.Search
          aria-label="Quick filter — alert key, rule, run, signal, work item or entity id"
          placeholder="Quick filter: key / rule / run / signal id…"
          allowClear
          style={{ width: 300 }}
          value={quick}
          onChange={(e) => setQuick(e.target.value)}
        />
      </div>

      <Table<AlertView>
        rowKey="alertId"
        size="small"
        loading={loading && visible.length > 0}
        columns={columns}
        dataSource={visible}
        pagination={visible.length > 25 ? { pageSize: 25, showSizeChanger: false } : false}
        onRow={(a) => ({
          onClick: () => onOpen(a),
          style: { cursor: 'pointer' },
          tabIndex: 0,
          'aria-label': `Alert ${a.alertKey} — open detail`,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') onOpen(a);
          },
        })}
        locale={{
          emptyText: loading ? (
            <TableSkeleton label="Loading alerts" />
          ) : (
            <div style={{ padding: '24px 0' }}>
              <CheckCircleOutlined
                style={{ fontSize: 22, color: 'var(--ol-text-tertiary)' }}
                aria-hidden
              />
              <div style={{ marginTop: 8, fontWeight: 600 }}>No alerts</div>
              <Text type="secondary" style={{ fontSize: 12.5 }}>
                An empty register is a good day, not an error — nothing the intake saw needs
                attention under the current filters.
              </Text>
            </div>
          ),
        }}
      />
    </div>
  );
}
